import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {fetchMedicalCard} from "../../store/slices/Health_Insurance_Card/HealthCardSlice";
import type {AppDispatch, RootState} from "../../store/store";
import {AlertCircle} from "lucide-react";
import { Link } from "react-router-dom";

const Health_Insurance_Card: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();


  const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';


  const translations = {
    az: {
      title: "Tibbi Sığorta şəhadətnaməsi",
      subtitle: "Şəhadətnamə məlumatları aşağıdakı kimidir",
      loading: "Məlumatlar yüklənir...",
      errorTitle: "Xəta baş verdi",
      noData: "Tibbi sığorta məlumatı tapılmadı",
      certificateNumber: "Şəhadətnamə nömrəsi",
      startDate: "Başlama tarixi",
      endDate: "Bitmə tarixi",
      myApplications: "Müraciətlərim",
      doctorAppointment: "Həkimə qeydiyyat",
      clinicsPharmacies: "Klinika və apteklər",
      insuranceRules: "Tibbi Sığorta Qaydaları",
      insurer: "Sığortaçı",
      fullName: "Ad, Soyad və Ata adı",
      birthDate: "Doğum tarixi",
      finCode: "FİN Kod",
      mobileNumber: "Mobil nömrə",
      insuredCompany: "Sığortalı şirkət",
      insuranceProgram: "Sığorta proqramı",
      policyCertificateNumber: "Sığorta şəhadətnamə nömrəsi",
      insurancePeriod: "Sığorta müddəti",
      statusActive: "Qüvvədədir",
      emergencyContact: "Təcili əlaqə",
      emergencyMedical: "Təcili tibbi yardım",
      dispatcherService: "Dispetçer xidməti",
      coverages: "Təminatlar",
      rulesModalTitle: "Sığorta Qaydaları",
      noCertificatesTitle: "Heç bir şəhadətnamə tapılmadı",
      noCertificatesDesc: "Hələlik tibbi sığorta şəhadətnaməniz yoxdur."
    },
    ru: {
      title: "Медицинский страховой полис",
      subtitle: "Информация о полисе следующая",
      loading: "Загрузка данных...",
      errorTitle: "Произошла ошибка",
      noData: "Информация о медицинской страховке не найдена",
      certificateNumber: "Номер полиса",
      startDate: "Дата начала",
      endDate: "Дата окончания",
      myApplications: "Мои обращения",
      doctorAppointment: "Запись к врачу",
      clinicsPharmacies: "Клиники и аптеки",
      insuranceRules: "Правила страхования",
      insurer: "Страховщик",
      fullName: "Имя, Фамилия и Отчество",
      birthDate: "Дата рождения",
      finCode: "ФИН Код",
      mobileNumber: "Мобильный номер",
      insuredCompany: "Застрахованная компания",
      insuranceProgram: "Программа страхования",
      policyCertificateNumber: "Номер страхового полиса",
      insurancePeriod: "Срок страхования",
      statusActive: "Активен",
      emergencyContact: "Экстренная связь",
      emergencyMedical: "Скорая медицинская помощь",
      dispatcherService: "Диспетчерская служба",
      coverages: "Покрытия",
      rulesModalTitle: "Правила страхования",
      noCertificatesTitle: "Медицинский страховой полис не найден",
      noCertificatesDesc: "У вас пока нет медицинского страхового полиса.",
    },
    en: {
      title: "Medical Insurance Certificate",
      subtitle: "Certificate information is as follows",
      loading: "Loading data...",
      errorTitle: "An error occurred",
      noData: "Medical insurance information not found",
      certificateNumber: "Certificate number",
      startDate: "Start date",
      endDate: "End date",
      myApplications: "My Applications",
      doctorAppointment: "Doctor Appointment",
      clinicsPharmacies: "Clinics & Pharmacies",
      insuranceRules: "Medical Insurance Rules",
      insurer: "Insurer",
      fullName: "Full Name",
      birthDate: "Date of birth",
      finCode: "FIN Code",
      mobileNumber: "Mobile number",
      insuredCompany: "Insured company",
      insuranceProgram: "Insurance program",
      policyCertificateNumber: "Insurance certificate number",
      insurancePeriod: "Insurance period",
      statusActive: "Active",
      emergencyContact: "Emergency contact",
      emergencyMedical: "Emergency medical care",
      dispatcherService: "Dispatcher service",
      coverages: "Coverages",
      rulesModalTitle: "Insurance Rules",
      noCertificatesTitle: "No Medical Insurance Certificate Found",
      noCertificatesDesc: "You don't have a medical insurance certificate yet.",
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const {medicalCard, loading, error} = useSelector(
    (state: RootState) => state.healthInsurance
  );

  useEffect(() => {
    dispatch(fetchMedicalCard());
  }, [dispatch]);



  if (loading) {
    return (
      <div className=" flex  justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>

        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600"/>
          <div>
            <h3 className="font-semibold text-red-900">{t.errorTitle}</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!medicalCard) {
    return (
      <div className="min-h-screen">
        <h1 className="text-stone-800 text-3xl font-semibold mb-2">
          {t.title}
        </h1>
        <p className="text-stone-600 leading-relaxed text-base tracking-wide mb-6">
          {t.subtitle}
        </p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-file-medical text-gray-400 text-2xl"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.noCertificatesTitle}
            </h3>
            <p className="text-gray-500">
              {t.noCertificatesDesc}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string): string => {
    return dateString.replace(/-/g, '.');
  };

  const getStatusBadge = () => {
    const statusMap: Record<string, { text: string; color: string }> = {
      Active: {text: t.statusActive, color: "bg-green-600"}
    };
    const status = statusMap[medicalCard.status] || statusMap.Active;
    return (
      <button className={`${status.color} text-white px-3 py-1 rounded-full text-xs w-fit`}>
        {status.text}
      </button>
    );
  };

  const handleDocumentOpen = () => {
    if (medicalCard?.product_document) {
      const doc = medicalCard.product_document;

      if (doc.startsWith('http://') || doc.startsWith('https://')) {
        window.open(doc, '_blank');
        return;
      }

      let pdfData = '';
      if (doc.startsWith('data:application/pdf;base64,')) {
        pdfData = doc;
      } else {
        pdfData = `data:application/pdf;base64,${doc}`;
      }

      try {
        const byteCharacters = atob(pdfData.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, '_blank');

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('PDF açılmadı:', error);
        alert('PDF açıla bilmədi. Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  return (
    <div className="min-h-screen">

        <h1 className=" text-stone-800 text-3xl font-semibold ">
          {t.title}
        </h1>
        <p className=" text-stone-600 leading-relaxed text-base tracking-wide mb-6">
          {t.subtitle}
        </p>


      <div className="rounded-2xl w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-6">
          {/* Card Section */}
          <div className="w-full sm:w-auto sm:flex-shrink-0">
            <div
              className="bg-colorpink rounded-xl p-6 text-white shadow-lg h-auto sm:h-96 w-full sm:w-72 flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div className="w-full">
                  <p className="text-sm sm:text-base ">{t.certificateNumber}</p>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xl  font-semibold break-all">
                      {medicalCard.policy_number}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 sm:gap-6 mt-6 sm:mt-0">
                <div>
                  <p className="text-xs sm:text-sm opacity-90 mb-1">
                    {t.startDate}
                  </p>
                  <p className="text-base sm:text-lg font-semibold">
                    {formatDate(medicalCard.start_date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm opacity-90 mb-1">
                    {t.endDate}
                  </p>
                  <p className="text-base sm:text-lg font-semibold">
                    {formatDate(medicalCard.end_date)}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-2 sm:space-y-4 mt-4 sm:mt-8">
              <Link
                to="/medicalrequests"
                className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
              >
  <span className="w-8 text-center">
    <i className="fas fa-clipboard-list text-base sm:text-lg mr-2"></i>
  </span>
                <span>{t.myApplications}</span>
              </Link>

              {/* Həkimə qeydiyyat Button */}
              <Link
                to="/doctor-appointment"
                className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
              >
            <span className="w-8 text-center">
              <i className="fas fa-user-md text-base sm:text-lg mr-2"></i>
            </span>
                <span>{t.doctorAppointment}</span>
              </Link>

              {/* Klinika və apteklər Button */}
              <Link
                to="/branches"
                className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
              >
            <span className="w-8 text-center">
              <i className="fas fa-hospital text-base sm:text-lg mr-2"></i>
            </span>
                <span>{t.clinicsPharmacies}</span>
              </Link>

              {/* Tibbi Sığorta Qaydaları Button */}
              <button
                onClick={handleDocumentOpen}
                className="w-full sm:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 mt-8 text-sm sm:text-base transition-colors"
              >
                <span className="w-8 text-center">
                  <i className="fas fa-book text-base sm:text-lg mr-2"></i>
                </span>
                <span>{t.insuranceRules}</span>
              </button>

            </div>
          </div>
          {/* Details Section */}
          <div className="flex-1 p-4 sm:p-6 lg:p-4 bg-white rounded-xl border border-gray-200">
            <div className="space-y-4 sm:space-y-6">
              {/* Insurance Company */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.insurer}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm sm:text-base text-gray-800 font-medium">
                    {medicalCard.insurer_name}
                  </span>
                </div>
              </div>

              {/* Personal Info */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.fullName}
                </h3>
                <p className="text-sm sm:text-base text-gray-800 font-medium">
                  {medicalCard.insured_person_name}
                </p>
              </div>

              {/* Birth Date & FIN */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                  {t.birthDate}
                </h3>
                <p className="text-sm sm:text-base text-gray-800">
                  {formatDate(medicalCard.insured_person_birth_date)}
                </p>
              </div>
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                  {t.finCode}
                </h3>
                <p className="text-sm sm:text-base text-gray-800">
                  {medicalCard.insured_person_pin}
                </p>
              </div>

              {/* Mobile Number */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                  {t.mobileNumber}
                </h3>
                <p className="text-sm sm:text-base text-gray-800">
                  {medicalCard.insured_person_mobile_number}
                </p>
              </div>

              {/* Company */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.insuredCompany}
                </h3>
                <p className="text-sm sm:text-base text-gray-800">
                  {medicalCard.insured_company_name}
                </p>
              </div>

              {/* Insurance Program */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.insuranceProgram}
                </h3>
                <p className="text-sm sm:text-base text-gray-800">
                  {medicalCard.medical_insurance_program}
                </p>
              </div>

              {/* Policy Number */}
              <div>
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.policyCertificateNumber}
                </h3>
                <p className="text-sm sm:text-base text-gray-800 font-mono break-all">
                  {medicalCard.policy_number}
                </p>
              </div>

              {/* Insurance Period & Status */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                    {t.insurancePeriod}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-800">
                    {formatDate(medicalCard.start_date)} -{" "}
                    {formatDate(medicalCard.end_date)}
                  </p>
                </div>
                {getStatusBadge()}
              </div>

              {/* Emergency Contact Buttons */}
              <div className="space-y-3">
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                  {t.emergencyContact}
                </h3>
                <a
                  href={`tel:${medicalCard.emergency_call_number}`}
                  className="w-full bg-gray-200 border border-gray-400 hover:bg-colorpink hover:text-white hover:border-pink-800 text-gray-800 py-3 px-4 rounded-xl font-medium flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <i className="fas fa-ambulance text-base sm:text-lg"></i>
                    <span className="text-sm sm:text-base">
                                           {t.emergencyMedical}
                                            </span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold">
                    {medicalCard.emergency_call_number}
                  </span>
                </a>

                <a
                  href={`tel:${medicalCard.call_center_number}`}
                  className="w-full bg-gray-200 border border-gray-400 hover:bg-colorpink hover:text-white hover:border-pink-800 text-gray-800 py-3 px-4 rounded-xl font-medium flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <i className="fas fa-phone text-base sm:text-lg"></i>
                    <span className="text-sm sm:text-base">
                      {t.dispatcherService}
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold">
                    {medicalCard.call_center_number}
                  </span>
                </a>
              </div>

              {/* Coverage List */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-3">
                  {t.coverages}
                </h3>
                <ul className="space-y-2 text-xs sm:text-sm text-gray-700 list-disc list-inside">
                  {medicalCard.medical_coverages.map((coverage: string, index: number) => (
                    <li key={index}>{coverage}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Health_Insurance_Card;